import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { marketingApi, salesHubApi } from '@/services/api';
import { useUIStore } from '@/store/uiStore';
import { formatMoney, formatDate, toLocalDateString } from '@/utils/currency';
import Sidebar from '@/components/common/Sidebar';
import CreateSalesOrderModal from '@/components/sales/CreateSalesOrderModal';
import QuotationPdfModal from '@/components/sales/QuotationPdfModal';
import QuotationQuickSaleModal from '@/components/pos/QuotationQuickSaleModal';
import Receipt from '@/components/pos/Receipt';
import PayByLinkModal from '@/components/pos/PayByLinkModal';
import DeleteSaleModal from '@/components/sales/DeleteSaleModal';
import BulkDeleteSalesModal from '@/components/sales/BulkDeleteSalesModal';
import ClearDataModal from '@/components/settings/ClearDataModal';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/services/api';
import { validatePhone } from '@/utils/mobileValidation';
import { downloadQuotationPdf, printQuotation } from '@/utils/quotationExportPdf';
import {
  downloadPickupListPdf,
  printPickupList,
  parseSaleScanCode,
  sanitizePrintText,
  extractUserPickupNotes,
  type PickupListSale,
} from '@/utils/pickupListPrint';
import {
  downloadInvoicePdf,
  printInvoicePdf,
  type InvoicePdfSale,
} from '@/utils/invoiceExportPdf';
import CameraScannerModal from '@/components/pos/CameraScannerModal';
import toast from 'react-hot-toast';
import {
  FileText,
  Clock,
  RotateCcw,
  Search,
  Plus,
  Printer,
  MoreVertical,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Copy,
  Download,
  // Send,
  Edit2,
  X,
  Menu,
  ShoppingBag,
  ShoppingCart,
  SlidersHorizontal,
  Calendar,
  AlertTriangle,
  Loader2,
  ClipboardList,
  ScanBarcode,
  QrCode,
  MessageCircle,
} from 'lucide-react';

type TabType = 'quotes' | 'sales' | 'returns';

interface SalesHubItem {
  id: string;
  name: string;
  customer?: string;
  customer_name?: string;
  customer_contact?: string;
  total: number;
  paid_amount?: number;
  balance_amount?: number;
  status: string;
  payment_method?: string;
  created_by?: string;
  expected_date?: string;
  invoice_date?: string;
  delivery_date?: string;
  fulfillment_type?: string;
  date?: string;
  creation?: string;
  invoice_ref?: string;
  items?: any[];
  line_count?: number;
}

/** True when calendar date is after today (local). */
function isFutureCalendarDate(value?: string | null): boolean {
  if (!value) return false;
  const day = String(value).trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return false;
  return day > toLocalDateString();
}

interface ProductItem {
  item_code: string;
  item_name: string;
  line_comment?: string;
  quantity: number;
  available_stock: number;
  rate: number;
  amount: number;
  cost_price?: number;
  /** POS Item tax_rate percent (0, 5, 20, …) */
  tax_rate?: number;
  original_rate?: number;
  promo_amount?: number;
}

interface RecordDetail {
  id: string;
  name: string;
  tab: TabType;
  status: string;
  creation: string;
  invoice_date?: string;
  expected_date?: string;
  payment_method?: string;
  owner?: string;
  items: ProductItem[];
  financials: {
    subtotal: number;
    subtotal_original?: number;
    vat: number;
    discount?: number;
    discount_amount?: number;
    discount_total?: number;
    loyalty_discount?: number;
    discount_type?: string;
    discount_value?: number;
    discount_percent?: number;
    promo_total?: number;
    write_off_amount?: number;
    prices_include_tax?: boolean;
    total: number;
    paid: number;
    balance: number;
    profit: number;
    profit_margin: number;
  };
  loyalty?: {
    earned?: number;
    redeemed?: number;
    balance?: number;
    loyalty_discount?: number;
  };
  customer?: {
    name?: string;
    customer_name?: string;
    mobile?: string;
    email?: string;
    address?: string;
    city?: string;
    province?: string;
    postal_code?: string;
  };
  valid_till?: string;
  delivery_date?: string;
  fulfillment_type?: string;
  notes?: string;
  discount_amount?: number;
  discount_type?: string;
  discount_value?: number;
  discount_percent?: number;
  promo_total?: number;
  promo_campaigns?: string;
  prices_include_tax?: boolean;
}

export default function SalesHubPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toggleSidebar } = useUIStore();

  // Tab State
  const tabParam = (searchParams.get('tab') as TabType) || 'quotes';
  const urlSearch = searchParams.get('search') || searchParams.get('customer') || '';
  const urlStatus = searchParams.get('status');

  const [activeTab, setActiveTab] = useState<TabType>(tabParam);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState(urlSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);
  const [statusFilter, setStatusFilter] = useState(urlStatus || (tabParam === 'quotes' ? 'Active' : 'All'));
  const [hideWalkIn, setHideWalkIn] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [dateRangeLabel, setDateRangeLabel] = useState('All time');
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Selection & Detail Drawer State
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  /** Bulk checkbox selection (sales tab — pickup lists) */
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [pickupMenuOpen, setPickupMenuOpen] = useState(false);
  const [pickupBusy, setPickupBusy] = useState(false);
  const [invoiceMenuOpen, setInvoiceMenuOpen] = useState(false);
  const [showSaleScanner, setShowSaleScanner] = useState(false);
  const [saleScanPending, setSaleScanPending] = useState(false);
  const [saleScanError, setSaleScanError] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createModalMode, setCreateModalMode] = useState<'quote' | 'sale'>('quote');
  const [editRecordData, setEditRecordData] = useState<RecordDetail | null>(null);
  const [isQuotationPdfModalOpen, setIsQuotationPdfModalOpen] = useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [payByLinkInvoice, setPayByLinkInvoice] = useState<{ name: string; amount?: number } | null>(null);
  const [saleModalQuote, setSaleModalQuote] = useState<any | null>(null);
  const [createdInvoiceReceipt, setCreatedInvoiceReceipt] = useState<string | null>(null);
  const [deleteSaleTarget, setDeleteSaleTarget] = useState<{ id: string; name?: string; customer?: string; total?: number } | null>(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showClearAllSalesModal, setShowClearAllSalesModal] = useState(false);

  const { role } = useAuthStore();
  const isAdmin = Boolean(
    role === 'Company Admin' ||
    role === 'Company Admin Owner' ||
    role === 'System Manager' ||
    role === 'Administrator' ||
    role?.toLowerCase().includes('admin')
  );
  const canViewCancelled = isAdmin || role === 'Manager';

  // Sync tab with URL — always pull fresh list/counts on switch
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    setSearchTerm('');
    setDebouncedSearch('');
    setPage(1);
    setSelectedDocId(null);
    setCheckedIds([]);
    setPickupMenuOpen(false);
    setStatusFilter(tab === 'quotes' ? 'Active' : 'All');
    void queryClient.invalidateQueries({ queryKey: ['salesHubList'] });
    void queryClient.invalidateQueries({ queryKey: ['salesHubCounts'] });
  };

  const toggleChecked = (id: string) => {
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleCheckAllOnPage = (pageIds: string[]) => {
    const allChecked = pageIds.length > 0 && pageIds.every((id) => checkedIds.includes(id));
    if (allChecked) {
      setCheckedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setCheckedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const toPickupSale = (detail: RecordDetail): PickupListSale => ({
    name: detail.name,
    invoice_date: detail.invoice_date,
    creation: detail.creation,
    payment_method: detail.payment_method,
    // Only the user-typed Notes field — never Order Type / Address / Payment Term metadata
    notes: extractUserPickupNotes(detail.notes),
    customer: detail.customer,
    items: (detail.items || []).map((it) => ({
      item_code: it.item_code,
      item_name: it.item_name,
      quantity: it.quantity,
      rate: it.rate,
      amount: it.amount,
    })),
    financials: {
      total: detail.financials?.total,
      subtotal: detail.financials?.subtotal,
    },
  });

  const toInvoicePdfSale = (detail: RecordDetail): InvoicePdfSale => ({
    name: detail.name,
    invoice_date: detail.invoice_date,
    creation: detail.creation,
    payment_method: detail.payment_method,
    notes: detail.notes,
    owner: detail.owner,
    status: detail.status,
    customer: detail.customer,
    items: (detail.items || []).map((it) => ({
      item_code: it.item_code,
      item_name: it.item_name,
      line_comment: it.line_comment || '',
      quantity: it.quantity,
      rate: it.rate,
      amount: it.amount,
      tax_rate: it.tax_rate,
      original_rate: it.original_rate,
      promo_amount: it.promo_amount,
    })),
    payments: detail.payment_method
      ? [
          {
            payment_method: detail.payment_method,
            amount: detail.financials?.paid ?? detail.financials?.total,
          },
        ]
      : [],
    financials: {
      subtotal: detail.financials?.subtotal,
      subtotal_original: detail.financials?.subtotal_original,
      vat: detail.financials?.vat,
      discount_amount:
        detail.financials?.discount_amount ?? detail.discount_amount ?? detail.financials?.discount,
      promo_total: detail.financials?.promo_total ?? detail.promo_total,
      write_off_amount: detail.financials?.write_off_amount,
      prices_include_tax:
        detail.financials?.prices_include_tax ?? detail.prices_include_tax,
      total: detail.financials?.total,
      paid_amount: detail.financials?.paid,
    },
  });

  const pickupProfileOpts = () => {
    const p = profile as Record<string, any> | undefined;
    return {
      companyName: sanitizePrintText(p?.company_name || p?.company?.name || p?.company) || 'Company',
      companyAddress: sanitizePrintText(p?.address),
      companyPhone: sanitizePrintText(p?.phone || p?.mobile || p?.contact_no),
      companyLogo: p?.company_logo || '',
      appOrigin: typeof window !== 'undefined' ? window.location.origin : '',
    };
  };

  const invoiceProfileOpts = () => {
    const p = profile as Record<string, any> | undefined;
    return {
      companyName: sanitizePrintText(p?.company_name || p?.company?.name || p?.company) || 'Company',
      companyAddress: sanitizePrintText(p?.address),
      companyPhone: sanitizePrintText(p?.phone || p?.mobile || p?.contact_no),
      companyEmail: sanitizePrintText(p?.email),
      companyLogo: p?.company_logo || '',
      receiptTemplate: p?.receipt_template_settings || p?.pos_settings?.receipt_template_settings,
      country: p?.country || p?.pos_settings?.country || null,
    };
  };

  const openSaleFromCode = async (raw: string): Promise<boolean> => {
    const id = parseSaleScanCode(raw);
    if (!id) {
      setSaleScanError('Could not read an order from that code');
      toast.error('Could not read an order from that code');
      return false;
    }
    setSaleScanPending(true);
    setSaleScanError('');
    try {
      setActiveTab('sales');
      setStatusFilter('All');
      setPage(1);
      setSearchTerm(id);
      setDebouncedSearch(id);
      setSearchParams({ tab: 'sales', invoice: id }, { replace: true });
      try {
        await salesHubApi.getDetail('sales', id);
        setSelectedDocId(id);
        toast.success(`Opened order ${id}`);
      } catch {
        setSelectedDocId(null);
        toast.success(`Searching for ${id}`);
      }
      return true;
    } finally {
      setSaleScanPending(false);
    }
  };

  const runPickupList = async (
    ids: string[],
    mode: 'download' | 'print',
    includePrice: boolean,
  ) => {
    if (!ids.length) {
      toast.error('Select at least one sale');
      return;
    }
    setPickupBusy(true);
    setPickupMenuOpen(false);
    setInvoiceMenuOpen(false);
    const toastId = toast.loading(
      mode === 'download'
        ? `Preparing pick list PDF (${ids.length})…`
        : `Preparing pick list print (${ids.length})…`,
    );
    try {
      const details = await Promise.all(
        ids.map(async (id) => {
          const res = await salesHubApi.getDetail('sales', id);
          return toPickupSale(res.data.message as RecordDetail);
        }),
      );
      const opts = { includePrice, ...pickupProfileOpts() };
      if (mode === 'download') {
        await downloadPickupListPdf(details, opts);
        toast.success(
          includePrice
            ? `Downloaded ${ids.length} pick list(s) with prices`
            : `Downloaded ${ids.length} pick list(s) without prices`,
          { id: toastId },
        );
      } else {
        await printPickupList(details, opts);
        toast.success(
          includePrice
            ? `Opened ${ids.length} pick list(s) with prices for printing`
            : `Opened ${ids.length} pick list(s) without prices for printing`,
          { id: toastId },
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate pick list', { id: toastId });
    } finally {
      setPickupBusy(false);
    }
  };

  const runInvoiceBulk = async (ids: string[], mode: 'download' | 'print') => {
    if (!ids.length) {
      toast.error('Select at least one sale');
      return;
    }
    setPickupBusy(true);
    setPickupMenuOpen(false);
    setInvoiceMenuOpen(false);
    const toastId = toast.loading(
      mode === 'download'
        ? `Preparing invoice PDF (${ids.length})…`
        : `Preparing invoice print (${ids.length})…`,
    );
    try {
      const details = await Promise.all(
        ids.map(async (id) => {
          const res = await salesHubApi.getDetail('sales', id);
          return toInvoicePdfSale(res.data.message as RecordDetail);
        }),
      );
      if (mode === 'download') {
        await downloadInvoicePdf(details, invoiceProfileOpts());
        toast.success(
          ids.length === 1 ? 'Invoice PDF downloaded' : `Downloaded ${ids.length} invoice PDF(s)`,
          { id: toastId },
        );
      } else {
        await printInvoicePdf(details, invoiceProfileOpts());
        toast.success(
          ids.length === 1
            ? 'Opened invoice for printing'
            : `Opened ${ids.length} invoice(s) for printing`,
          { id: toastId },
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate invoice PDF', { id: toastId });
    } finally {
      setPickupBusy(false);
    }
  };

  const toQuotePdfPayload = (detail: RecordDetail) => ({
    name: detail.name,
    customer_name: detail.customer?.customer_name || detail.customer?.name,
    customer: detail.customer?.name,
    contact_display: [
      detail.customer?.mobile,
      detail.customer?.email,
      detail.customer?.address,
      detail.customer?.city,
      detail.customer?.postal_code,
    ]
      .filter(Boolean)
      .join(' · '),
    creation: detail.creation,
    valid_till: detail.valid_till || detail.expected_date,
    owner: detail.owner,
    net_total: detail.financials?.subtotal,
    total_taxes_and_charges: detail.financials?.vat,
    discount_amount: detail.financials?.discount_amount ?? detail.discount_amount ?? 0,
    discount_percent: detail.financials?.discount_percent ?? detail.discount_percent,
    notes: detail.notes,
    grand_total: detail.financials?.total,
    items: (detail.items || []).map((it) => ({
      item_name: it.item_name,
      item_code: it.item_code,
      line_comment: it.line_comment || '',
      quantity: it.quantity,
      rate: it.rate,
      amount: it.amount,
    })),
  });

  const runQuoteBulk = async (ids: string[], mode: 'download' | 'print') => {
    if (!ids.length) {
      toast.error('Select at least one quote');
      return;
    }
    setPickupBusy(true);
    setPickupMenuOpen(false);
    const toastId = toast.loading(
      mode === 'download'
        ? `Preparing quotation PDF (${ids.length})…`
        : `Preparing quotation print (${ids.length})…`,
    );
    try {
      const details = await Promise.all(
        ids.map(async (id) => {
          const res = await salesHubApi.getDetail('quotes', id);
          return toQuotePdfPayload(res.data.message as RecordDetail);
        }),
      );
      if (mode === 'download') {
        await downloadQuotationPdf(details, profile);
        toast.success(
          ids.length === 1
            ? 'Quotation PDF downloaded'
            : `Downloaded ${ids.length} quotation PDF(s)`,
          { id: toastId },
        );
      } else {
        await printQuotation(details, profile);
        toast.success(
          ids.length === 1
            ? 'Opened quotation for printing'
            : `Opened ${ids.length} quotation(s) for printing`,
          { id: toastId },
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to generate quotation PDF', { id: toastId });
    } finally {
      setPickupBusy(false);
    }
  };

  useEffect(() => {
    const tabInUrl = searchParams.get('tab') as TabType;
    if (tabInUrl && tabInUrl !== activeTab) {
      setActiveTab(tabInUrl);
    }
    const statusInUrl = searchParams.get('status');
    if (statusInUrl) {
      if (statusInUrl !== statusFilter) setStatusFilter(statusInUrl);
    } else if (tabInUrl && tabInUrl !== activeTab) {
      setStatusFilter(tabInUrl === 'quotes' ? 'Active' : 'All');
    }
    const invoiceInUrl = searchParams.get('invoice') || searchParams.get('open') || '';
    const searchInUrl = searchParams.get('search') || searchParams.get('customer') || '';
    if (invoiceInUrl) {
      if (activeTab !== 'sales' && tabInUrl !== 'sales') {
        setActiveTab('sales');
      }
      if (searchTerm !== invoiceInUrl) {
        setSearchTerm(invoiceInUrl);
        setDebouncedSearch(invoiceInUrl);
      }
      if (statusFilter !== 'All') setStatusFilter('All');
      if (selectedDocId !== invoiceInUrl) setSelectedDocId(invoiceInUrl);
    } else if (searchInUrl !== searchTerm) {
      setSearchTerm(searchInUrl);
      setDebouncedSearch(searchInUrl);
    }
  }, [searchParams]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!pickupMenuOpen && !invoiceMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPickupMenuOpen(false);
        setInvoiceMenuOpen(false);
      }
    };
    const onClick = () => {
      setPickupMenuOpen(false);
      setInvoiceMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    // Defer so the opening click doesn't immediately close
    const t = window.setTimeout(() => window.addEventListener('click', onClick), 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('click', onClick);
    };
  }, [pickupMenuOpen, invoiceMenuOpen]);

  // Resolve from/to dates based on preset (local calendar — SD-UK-034/035)
  const { fromDate, toDate } = useMemo(() => {
    const now = new Date();
    const to = toLocalDateString(now);
    if (dateFilter === 'today') {
      return { fromDate: to, toDate: to };
    } else if (dateFilter === 'week') {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      return { fromDate: toLocalDateString(d), toDate: to };
    } else if (dateFilter === 'month') {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return { fromDate: toLocalDateString(d), toDate: to };
    } else if (dateFilter === 'year') {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 1);
      return { fromDate: toLocalDateString(d), toDate: to };
    }
    return { fromDate: undefined, toDate: undefined };
  }, [dateFilter]);

  // Badge Counts Query
  const { data: countsData } = useQuery({
    queryKey: ['salesHubCounts'],
    queryFn: async () => {
      const res = await salesHubApi.getCounts();
      return res.data?.message || { quotes: 0, sales: 0, returns: 0 };
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  // Profile Query for Branded PDF Quotation
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getMyProfile();
      return res.data?.message || res.data;
    },
  });

  const { data: whatsappReadyPayload } = useQuery({
    queryKey: ['marketingWhatsappReady'],
    queryFn: async () => (await marketingApi.marketingWhatsappReady()).data?.message,
    staleTime: 60_000,
    retry: 1,
  });
  const whatsappMarketingReady = Boolean(whatsappReadyPayload?.ready);
  const [whatsappSendBusy, setWhatsappSendBusy] = useState(false);

  const canSendInvoiceWhatsapp = (detail?: RecordDetail | null) => {
    if (!whatsappMarketingReady || !detail || activeTab !== 'sales') return false;
    const mobile = (detail.customer?.mobile || '').trim();
    if (!mobile) return false;
    const country = (profile as { country?: string } | undefined)?.country || 'GB';
    return !validatePhone(mobile, country, { required: true });
  };

  const sendInvoiceWhatsapp = async (invoiceId: string) => {
    setWhatsappSendBusy(true);
    try {
      await marketingApi.sendInvoicePdfWhatsapp(invoiceId);
      toast.success('Invoice PDF sent on WhatsApp');
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string; _server_messages?: string } } })?.response?.data
          ?.message ||
        (e instanceof Error ? e.message : 'Could not send WhatsApp invoice');
      toast.error(String(msg).slice(0, 200));
    } finally {
      setWhatsappSendBusy(false);
    }
  };

  // Main List Query — always refetch when remounting / returning from POS
  const { data: listData, isLoading: isListLoading } = useQuery({
    queryKey: ['salesHubList', activeTab, debouncedSearch, statusFilter, fromDate, toDate, page, hideWalkIn],
    queryFn: async () => {
      const res = await salesHubApi.getList({
        tab: activeTab,
        search: debouncedSearch,
        status: statusFilter === 'All' ? undefined : statusFilter,
        from_date: fromDate,
        to_date: toDate,
        page,
        limit,
        hide_walk_in: activeTab === 'sales' ? hideWalkIn : undefined,
      });
      return res.data?.message || { items: [], total: 0, total_pages: 1, summary: { total_value: 0, total_count: 0 } };
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Refetch whenever the active hub tab changes (incl. URL-driven switches)
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['salesHubList'] });
    void queryClient.invalidateQueries({ queryKey: ['salesHubCounts'] });
  }, [activeTab, queryClient]);

  const items: SalesHubItem[] = listData?.items || [];
  const totalItems = listData?.total || 0;
  const totalPages = listData?.total_pages || 1;
  const summary = listData?.summary || { total_value: 0, total_count: 0 };

  // Deselect if currently selected item no longer exists in items list
  useEffect(() => {
    if (selectedDocId && !items.some((it) => it.name === selectedDocId)) {
      setSelectedDocId(null);
    }
  }, [items, selectedDocId]);

  const pageIds = useMemo(() => items.map((it) => it.name), [items]);
  const allPageChecked = pageIds.length > 0 && pageIds.every((id) => checkedIds.includes(id));
  const somePageChecked = pageIds.some((id) => checkedIds.includes(id));

  // Selected Detail Query — always refetch so tax-inclusive / promo fields stay current
  const { data: detailData, isLoading: isDetailLoading } = useQuery({
    queryKey: ['salesHubDetail', activeTab, selectedDocId],
    queryFn: async () => {
      if (!selectedDocId) return null;
      const res = await salesHubApi.getDetail(activeTab, selectedDocId);
      return res.data?.message as RecordDetail;
    },
    enabled: !!selectedDocId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Action Mutations
  const duplicateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await salesHubApi.duplicateQuote(id);
      return res.data?.message;
    },
    onSuccess: (newDocName) => {
      toast.success(`Quotation duplicated as ${newDocName}`);
      queryClient.invalidateQueries({ queryKey: ['salesHubList'] });
      queryClient.invalidateQueries({ queryKey: ['salesHubCounts'] });
      setSelectedDocId(newDocName);
      setActionMenuOpen(false);
    },
    onError: () => toast.error('Failed to duplicate quotation'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await salesHubApi.deleteQuote(id);
    },
    onSuccess: () => {
      toast.success('Quotation deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['salesHubList'] });
      queryClient.invalidateQueries({ queryKey: ['salesHubCounts'] });
      setDeleteConfirmModal(null);
      setActionMenuOpen(false);
    },
    onError: () => toast.error('Failed to delete quotation'),
  });

  // Tab Badge / Info helper
  const getTabLabel = (tab: TabType) => {
    switch (tab) {
      case 'quotes':
        return 'Quotes';
      case 'sales':
        return 'Sales';
      case 'returns':
        return 'Returns';
    }
  };

  const getMetricTitle = () => {
    switch (activeTab) {
      case 'quotes':
        return { value: 'Total Quote Value', count: 'Total Number of Quotes' };
      case 'sales':
        return { value: 'Total Sales Value', count: 'Total Number of Sales' };
      case 'returns':
        return { value: 'Total Return Value', count: 'Total Number of Returns' };
    }
  };

  const formatShortId = (name: string) => {
    if (!name) return '';
    // If name is like ACC-SINV-2026-00013, show 00013 or last 6 characters
    const parts = name.split('-');
    if (parts.length > 1) {
      return parts[parts.length - 1];
    }
    return name.slice(-6);
  };

  const handlePrint = () => {
    if (!detailData) return;
    if (activeTab === 'quotes') {
      setIsQuotationPdfModalOpen(true);
    } else {
      setIsReceiptModalOpen(true);
    }
  };

  const receiptPreviewData = useMemo(() => {
    if (!detailData) return null;
    return {
      name: detailData.name,
      customer_name: detailData.customer?.customer_name || detailData.customer?.name || 'Walk-in Customer',
      invoice_date: detailData.invoice_date || detailData.creation,
      status: activeTab === 'returns' ? 'Refund' : detailData.status,
      item_total: detailData.financials.subtotal,
      service_total: 0,
      discount_amount: detailData.financials?.discount_amount ?? detailData.discount_amount ?? 0,
      tax_amount: detailData.financials.vat,
      net_total: detailData.financials.total,
      paid_amount: detailData.financials.paid || detailData.financials.total,
      balance_amount: detailData.financials.balance || 0,
      notes: detailData.notes || (activeTab === 'returns' ? 'Sales Return / Refund Receipt' : ''),
      items: detailData.items.map((it) => ({
        item_code: it.item_code,
        item_name: it.item_name,
        line_comment: it.line_comment || '',
        item_type: 'Product',
        quantity: it.quantity,
        rate: it.rate,
        amount: it.amount,
        tax_rate: it.tax_rate,
        original_rate: it.original_rate,
        promo_amount: it.promo_amount,
        variant_name: '',
      })),
      payments: [
        {
          payment_method: detailData.payment_method || (activeTab === 'returns' ? 'Refund' : 'Cash'),
          amount: detailData.financials.paid || detailData.financials.total,
        },
      ],
    };
  }, [detailData, activeTab]);

  return (
    <div className="flex h-screen bg-[#f8fafc] text-slate-800">
      <Sidebar />

      <div className="flex-1 flex flex-col h-screen overflow-hidden relative">
        {/* TOP LEVEL NAVIGATION TABS */}
        <header className="bg-white border-b border-slate-200/80 px-6 pt-4 pb-0 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSidebar}
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg shrink-0"
              >
                <Menu className="w-5 h-5" />
              </button>

              {/* Three Main Tabs */}
              <nav className="flex items-center space-x-1 sm:space-x-2">
                <button
                  type="button"
                  onClick={() => handleTabChange('quotes')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-all border-b-2 ${activeTab === 'quotes'
                    ? 'border-rose-600 text-slate-900 bg-rose-50/40'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}
                >
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span>Quotes</span>
                  {countsData?.quotes !== undefined && (
                    <span className="bg-rose-100/80 text-rose-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      {countsData.quotes}
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange('sales')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-all border-b-2 ${activeTab === 'sales'
                    ? 'border-rose-600 text-slate-900 bg-rose-50/40'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}
                >
                  <Clock className="w-4 h-4 text-slate-500" />
                  <span>Sales</span>
                  {countsData?.sales !== undefined && (
                    <span className="bg-slate-100 text-slate-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      {countsData.sales}
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange('returns')}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-all border-b-2 ${activeTab === 'returns'
                    ? 'border-rose-600 text-slate-900 bg-rose-50/40'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                    }`}
                >
                  <RotateCcw className="w-4 h-4 text-slate-500" />
                  <span>Returns</span>
                  {countsData?.returns !== undefined && (
                    <span className="bg-slate-100 text-slate-700 text-xs font-bold px-2 py-0.5 rounded-full">
                      {countsData.returns}
                    </span>
                  )}
                </button>
              </nav>
            </div>
          </div>
        </header>

        {/* SUBHEADER: TITLE, SEARCH, AND ACTION BUTTON */}
        <div className="bg-white border-b border-slate-200/60 px-6 py-3.5 flex flex-wrap items-center justify-between gap-4 shrink-0">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              {getTabLabel(activeTab)}
            </h1>
          </div>

          <div className="flex items-center gap-3 flex-1 sm:flex-initial justify-end">
            {/* Search + scan (sales) */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative w-full sm:w-64">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder={activeTab === 'sales' ? 'Search or scan order…' : 'Search...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && activeTab === 'sales') {
                      const parsed = parseSaleScanCode(searchTerm);
                      if (parsed && (searchTerm.includes('invoice=') || searchTerm.startsWith('SALEDESK:') || /^https?:/i.test(searchTerm))) {
                        e.preventDefault();
                        void openSaleFromCode(searchTerm);
                      }
                    }
                  }}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-slate-50/80 hover:bg-slate-100/60 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:bg-white transition-all placeholder:text-slate-400 min-h-[44px]"
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setDebouncedSearch('');
                      setSelectedDocId(null);
                      if (searchParams.has('search') || searchParams.has('customer') || searchParams.has('invoice') || searchParams.has('open')) {
                        const next = new URLSearchParams(searchParams);
                        next.delete('search');
                        next.delete('customer');
                        next.delete('invoice');
                        next.delete('open');
                        setSearchParams(next, { replace: true });
                      }
                    }}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {activeTab === 'sales' && (
                <>
                  <input
                    type="text"
                    value={scanInput}
                    onChange={(e) => setScanInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const code = scanInput.trim();
                        if (!code) return;
                        setScanInput('');
                        void openSaleFromCode(code);
                      }
                    }}
                    placeholder="Scan gun / order ID"
                    title="Focus here and scan a pick-list QR / barcode with a USB scanner"
                    className="hidden sm:block w-48 md:w-56 px-3 py-2.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-rose-500 min-h-[44px]"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSaleScanError('');
                      setShowSaleScanner(true);
                    }}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 min-h-[44px] min-w-[44px] border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold shrink-0"
                    title="Scan QR / barcode to open order"
                  >
                    <ScanBarcode className="w-5 h-5" />
                    <span className="hidden md:inline">Scan</span>
                  </button>
                </>
              )}
            </div>

            {/* Primary Action Button */}
            {activeTab === 'quotes' && (
              <button
                type="button"
                onClick={() => {
                  setEditRecordData(null);
                  setCreateModalMode('quote');
                  setIsCreateModalOpen(true);
                }}
                className="bg-slate-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors shrink-0"
              >
                <span>New quote</span>
              </button>
            )}

            {activeTab === 'sales' && (
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <>
                    <button
                      type="button"
                      disabled={checkedIds.length === 0}
                      onClick={() => setShowBulkDeleteModal(true)}
                      className="px-3 py-2 border border-amber-200 bg-amber-50/80 hover:bg-amber-100/80 text-amber-900 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-2xs shrink-0 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-amber-50/80"
                      title={
                        checkedIds.length
                          ? `Reset ${checkedIds.length} selected sale(s) only`
                          : 'Select one or more sales first'
                      }
                    >
                      <Trash2 className="w-3.5 h-3.5 text-amber-700" />
                      <span>
                        Reset Selected
                        {checkedIds.length > 0 ? ` (${checkedIds.length})` : ''}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowClearAllSalesModal(true)}
                      className="px-3 py-2 border border-rose-200 bg-rose-50/70 hover:bg-rose-100/80 text-rose-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-2xs shrink-0"
                      title="Wipe EVERY sale and return for this company (ignores selection)"
                    >
                      <RotateCcw className="w-3.5 h-3.5 text-rose-600" />
                      <span>Reset All Sales</span>
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => {
                    setEditRecordData(null);
                    setCreateModalMode('sale');
                    setIsCreateModalOpen(true);
                  }}
                  className="bg-slate-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors shrink-0"
                >
                  <Plus className="w-4 h-4" />
                  <span>New sale</span>
                </button>
              </div>
            )}

          </div>
        </div>

        {/* SCROLLABLE MAIN CONTENT */}
        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* SUMMARY METRICS ROW */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Coral Metric Card (Value) */}
            <div className="bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-2xl p-5 shadow-sm border border-rose-600/30">
              <div className="text-xs font-medium text-rose-100 uppercase tracking-wider mb-2">
                {getMetricTitle().value}
              </div>
              <div className="text-3xl font-extrabold tracking-tight">
                {formatMoney(summary.total_value)}
              </div>
            </div>

            {/* White Metric Card (Count) */}
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200/80">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
                {getMetricTitle().count}
              </div>
              <div className="text-3xl font-extrabold text-slate-900 tracking-tight">
                {summary.total_count}
              </div>
            </div>
          </div>

          {/* FILTER PILLS & DATE RANGE ROW */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {/* Status Filter Pills */}
              <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-100/90 rounded-xl border border-slate-200/60 shadow-2xs">
                {(activeTab === 'quotes'
                  ? [
                    { label: 'All', value: 'All' },
                    { label: 'Active', value: 'Active' },
                    { label: 'Converted', value: 'Converted' },
                    { label: 'Expired', value: 'Expired' },
                  ]
                  : activeTab === 'sales'
                    ? [
                      { label: 'All', value: 'All' },
                      { label: 'Paid', value: 'Paid' },
                      { label: 'Unpaid', value: 'Unpaid' },
                      { label: 'Partially Paid', value: 'Partially Paid' },
                      // Soft-voided sales stay on file for Admin/owner audit only
                      ...(canViewCancelled ? [{ label: 'Cancelled', value: 'Cancelled' }] : []),
                    ]
                    : [
                      { label: 'All', value: 'All' },
                      { label: 'Submitted', value: 'Submitted' },
                      { label: 'Draft', value: 'Draft' },
                    ]
                ).map((opt) => {
                  const isSelected = statusFilter === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setStatusFilter(opt.value);
                        setPage(1);
                      }}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${isSelected
                        ? 'bg-white text-slate-900 shadow-xs'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/60'
                        }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Hide Walk in Customer Checkbox */}
              {activeTab === 'sales' && (
                <label className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold border shadow-2xs cursor-pointer select-none transition-all ${hideWalkIn
                  ? 'bg-rose-50/70 border-rose-300 text-rose-900 ring-1 ring-rose-200'
                  : 'bg-white border-slate-200/90 text-slate-700 hover:border-slate-300 hover:bg-slate-50/60'
                  }`}>
                  <input
                    type="checkbox"
                    id="hide-walk-in-customer"
                    checked={hideWalkIn}
                    onChange={(e) => {
                      setHideWalkIn(e.target.checked);
                      setPage(1);
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600"
                  />
                  <span>Hide Walk in Customer</span>
                </label>
              )}
            </div>

            {/* Date Range Selector Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
                className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white border border-slate-200/90 hover:border-slate-300 rounded-lg text-xs font-semibold text-slate-700 shadow-2xs transition-colors"
              >
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <span>{dateRangeLabel}</span>
                <ChevronDown className="w-3 h-3 text-slate-400" />
              </button>

              {isDateDropdownOpen && (
                <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-30">
                  {[
                    { key: 'all', label: 'All time' },
                    { key: 'today', label: 'Today' },
                    { key: 'week', label: 'Last 7 days' },
                    { key: 'month', label: 'Last 30 days' },
                    { key: 'year', label: 'Last 1 year' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => {
                        setDateFilter(opt.key);
                        setDateRangeLabel(opt.label);
                        setIsDateDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-xs flex items-center justify-between ${dateFilter === opt.key ? 'bg-rose-50 text-rose-600 font-bold' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                    >
                      <span>{opt.label}</span>
                      {dateFilter === opt.key && <Check className="w-3.5 h-3.5 text-rose-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* BULK ACTIONS BAR — quotes */}
          {activeTab === 'quotes' && checkedIds.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-900 text-white rounded-2xl shadow-sm">
              <div className="flex items-center gap-3 text-sm">
                <FileText className="w-4.5 h-4.5 text-rose-300" />
                <span className="font-semibold">
                  {checkedIds.length} quote{checkedIds.length === 1 ? '' : 's'} selected
                </span>
                <button
                  type="button"
                  onClick={() => setCheckedIds([])}
                  className="text-xs text-slate-300 hover:text-white underline underline-offset-2"
                >
                  Clear selection
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={pickupBusy}
                  onClick={() => void runQuoteBulk(checkedIds, 'download')}
                  className="inline-flex items-center gap-2 px-3.5 py-2 bg-teal-500 hover:bg-teal-400 text-white rounded-lg text-xs font-semibold disabled:opacity-60"
                  title="Download quotation PDF (A4)"
                >
                  {pickupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  Download PDF
                </button>
                <button
                  type="button"
                  disabled={pickupBusy}
                  onClick={() => void runQuoteBulk(checkedIds, 'print')}
                  className="inline-flex items-center gap-2 px-3.5 py-2 bg-white text-slate-900 rounded-lg text-xs font-semibold hover:bg-slate-100 disabled:opacity-60"
                  title="Print quotation PDF (A4)"
                >
                  {pickupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                  Print
                </button>
              </div>
            </div>
          )}

          {/* BULK ACTIONS BAR (sales tab) */}
          {activeTab === 'sales' && checkedIds.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-slate-900 text-white rounded-2xl shadow-sm">
              <div className="flex items-center gap-3 text-sm">
                <ClipboardList className="w-4.5 h-4.5 text-rose-300" />
                <span className="font-semibold">
                  {checkedIds.length} sale{checkedIds.length === 1 ? '' : 's'} selected
                </span>
                <button
                  type="button"
                  onClick={() => setCheckedIds([])}
                  className="text-xs text-slate-300 hover:text-white underline underline-offset-2"
                >
                  Clear selection
                </button>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowBulkDeleteModal(true)}
                    className="inline-flex items-center gap-2 px-3.5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg text-xs font-semibold"
                    title="Reset only the selected sales"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Reset Selected Sales
                  </button>
                )}
                <div className="relative">
                  <button
                    type="button"
                    disabled={pickupBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPickupMenuOpen(false);
                      setInvoiceMenuOpen((o) => !o);
                    }}
                    className="inline-flex items-center gap-2 px-3.5 py-2 bg-teal-500 hover:bg-teal-400 text-white rounded-lg text-xs font-semibold disabled:opacity-60"
                    title="Print or download invoice PDF (A4)"
                  >
                    {pickupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                    Invoice
                    <ChevronDown className="w-3.5 h-3.5 text-white/80" />
                  </button>
                  {invoiceMenuOpen && (
                    <div
                      className="absolute right-0 mt-1.5 w-56 bg-white border border-slate-200 rounded-xl shadow-xl py-1 z-40 text-slate-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => void runInvoiceBulk(checkedIds, 'print')}
                      >
                        <Printer className="w-3.5 h-3.5 text-slate-500" />
                        Print Invoice
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => void runInvoiceBulk(checkedIds, 'download')}
                      >
                        <Download className="w-3.5 h-3.5 text-slate-500" />
                        Download PDF
                      </button>
                    </div>
                  )}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    disabled={pickupBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      setInvoiceMenuOpen(false);
                      setPickupMenuOpen((o) => !o);
                    }}
                    className="inline-flex items-center gap-2 px-3.5 py-2 bg-white text-slate-900 rounded-lg text-xs font-semibold hover:bg-slate-100 disabled:opacity-60"
                  >
                    {pickupBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
                    Pickup list
                    <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                  </button>
                  {pickupMenuOpen && (
                    <div
                      className="absolute right-0 mt-1.5 w-64 bg-white border border-slate-200 rounded-xl shadow-xl py-1 z-40 text-slate-800"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Download PDF (A4)
                      </div>
                      <button
                        type="button"
                        className="w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => runPickupList(checkedIds, 'download', true)}
                      >
                        <Download className="w-3.5 h-3.5 text-slate-500" />
                        With prices
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => runPickupList(checkedIds, 'download', false)}
                      >
                        <Download className="w-3.5 h-3.5 text-slate-500" />
                        Without prices
                      </button>
                      <div className="my-1 border-t border-slate-100" />
                      <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        Print
                      </div>
                      <button
                        type="button"
                        className="w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => runPickupList(checkedIds, 'print', true)}
                      >
                        <Printer className="w-3.5 h-3.5 text-slate-500" />
                        With prices
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-3.5 py-2 text-xs hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => runPickupList(checkedIds, 'print', false)}
                      >
                        <Printer className="w-3.5 h-3.5 text-slate-500" />
                        Without prices
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* MASTER-DETAIL SPLIT GRID */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* LEFT COLUMN: LIST TABLE (Full width by default, or split 7/8 cols when an item is selected) */}
            <div
              className={`bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-hidden transition-all duration-200 ${selectedDocId ? 'lg:col-span-7 xl:col-span-8' : 'col-span-12'
                }`}
            >
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50/70 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      <th className="py-3.5 pl-3 pr-2 w-14 text-center">
                        <label className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={
                              activeTab === 'sales' || activeTab === 'quotes'
                                ? allPageChecked
                                : items.length > 0 && selectedDocId !== null
                            }
                            ref={(el) => {
                              if (el && (activeTab === 'sales' || activeTab === 'quotes')) {
                                el.indeterminate = !allPageChecked && somePageChecked;
                              }
                            }}
                            onChange={() => {
                              if (activeTab === 'sales' || activeTab === 'quotes') {
                                toggleCheckAllOnPage(pageIds);
                              } else if (selectedDocId) {
                                setSelectedDocId(null);
                              } else if (items.length > 0) {
                                setSelectedDocId(items[0].name);
                              }
                            }}
                            className="w-6 h-6 rounded border-slate-400 text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600"
                            title={
                              activeTab === 'sales' || activeTab === 'quotes'
                                ? 'Select all on this page'
                                : 'Select'
                            }
                          />
                        </label>
                      </th>
                      <th className="py-3.5 px-3">ID</th>
                      <th className="py-3.5 px-3">
                        {activeTab === 'quotes' ? 'Expected' : 'Sale Date'}
                      </th>
                      {activeTab === 'sales' && (
                        <th className="py-3.5 px-3">Pick / Delivery</th>
                      )}
                      <th className="py-3.5 px-3">Customer</th>
                      <th className="py-3.5 px-3">Total</th>
                      <th className="py-3.5 px-3">Status</th>
                      <th className="py-3.5 px-3">Payment Method</th>
                      <th className="py-3.5 px-3">Created by</th>
                      <th className="py-3.5 pr-4 pl-2 text-right">
                        <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400 inline" />
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100 text-xs">
                    {isListLoading ? (
                      <tr>
                        <td colSpan={activeTab === 'sales' ? 10 : 9} className="py-12 text-center text-slate-400">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-rose-500" />
                          <span>Loading {activeTab}...</span>
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={activeTab === 'sales' ? 10 : 9} className="py-16 text-center text-slate-400">
                          <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                          <div className="text-sm font-semibold text-slate-700">No {activeTab} found</div>
                          <p className="text-xs text-slate-400 mt-1">Try clearing your filters or create a new entry.</p>
                        </td>
                      </tr>
                    ) : (
                      items.map((row) => {
                        const isSelected = selectedDocId === row.name;
                        const isChecked = checkedIds.includes(row.name);
                        const saleDateVal = row.invoice_date || row.expected_date || row.date;
                        const pickDateVal = row.delivery_date;
                        const futureSale = activeTab === 'sales' && isFutureCalendarDate(saleDateVal);
                        const futurePick = activeTab === 'sales' && isFutureCalendarDate(pickDateVal);
                        return (
                          <tr
                            key={row.name}
                            onClick={() => setSelectedDocId(isSelected ? null : row.name)}
                            className={`cursor-pointer transition-colors ${isSelected
                              ? 'bg-rose-50/40 font-medium'
                              : futureSale
                                ? 'bg-sky-50/70 hover:bg-sky-50'
                                : futurePick
                                  ? 'bg-amber-50/50 hover:bg-amber-50/80'
                                  : isChecked
                                    ? 'bg-slate-50/90'
                                    : 'hover:bg-slate-50/80'
                              }`}
                          >
                            <td className="py-2.5 pl-3 pr-2 text-center" onClick={(e) => e.stopPropagation()}>
                              <label className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={
                                    activeTab === 'sales' || activeTab === 'quotes'
                                      ? isChecked
                                      : isSelected
                                  }
                                  onChange={() => {
                                    if (activeTab === 'sales' || activeTab === 'quotes') {
                                      toggleChecked(row.name);
                                    } else {
                                      setSelectedDocId(isSelected ? null : row.name);
                                    }
                                  }}
                                  className="w-6 h-6 rounded border-slate-400 text-rose-600 focus:ring-rose-500 cursor-pointer accent-rose-600"
                                />
                              </label>
                            </td>

                            {/* Short Monospace ID */}
                            <td className="py-3.5 px-3">
                              <span className="font-mono text-slate-900 font-bold bg-slate-100/90 border border-slate-200/80 px-2 py-0.5 rounded text-[11px]">
                                {formatShortId(row.name)}
                              </span>
                            </td>

                            {/* Sale Date / Expected Date */}
                            <td className="py-3.5 px-3 text-slate-600 whitespace-nowrap">
                              {saleDateVal ? (
                                <span
                                  className={
                                    futureSale
                                      ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-sky-100 text-sky-800 border border-sky-200'
                                      : undefined
                                  }
                                  title={futureSale ? 'Future sale date' : undefined}
                                >
                                  {formatDate(saleDateVal)}
                                  {futureSale && <span className="opacity-70">Future</span>}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>

                            {activeTab === 'sales' && (
                              <td className="py-3.5 px-3 text-slate-600 whitespace-nowrap">
                                {pickDateVal ? (
                                  <div className="flex flex-col gap-0.5">
                                    <span
                                      className={
                                        futurePick
                                          ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-amber-100 text-amber-900 border border-amber-200 w-fit'
                                          : 'text-slate-600'
                                      }
                                      title={futurePick ? 'Future pick / delivery' : undefined}
                                    >
                                      {formatDate(pickDateVal)}
                                      {futurePick && <span className="opacity-70">Due</span>}
                                    </span>
                                    {row.fulfillment_type && (
                                      <span className="text-[10px] text-slate-400 font-medium">
                                        {row.fulfillment_type}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </td>
                            )}

                            {/* Customer Name + Contact */}
                            <td className="py-3.5 px-3">
                              <div className="font-bold text-slate-900 leading-tight">
                                {row.customer_name || row.customer || 'Walk-in'}
                              </div>
                              {row.customer_contact && (
                                <div className="text-[11px] text-slate-400 font-normal mt-0.5">
                                  {row.customer_contact}
                                </div>
                              )}
                            </td>

                            {/* Total Amount */}
                            <td className="py-3.5 px-3 font-bold text-slate-900 whitespace-nowrap">
                              {formatMoney(row.total)}
                            </td>

                            {/* Status Badge */}
                            <td className="py-3.5 px-3 whitespace-nowrap">
                              <span
                                className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${row.status === 'Active' || row.status === 'Draft' || row.status === 'quote'
                                  ? 'bg-blue-50 text-blue-700 border-blue-200/80'
                                  : row.status === 'Converted' || row.status === 'Paid'
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200/80'
                                    : row.status === 'Expired'
                                      ? 'bg-rose-50 text-rose-700 border-rose-200/80'
                                      : row.status === 'Unpaid' || row.status === 'Partially Paid'
                                        ? 'bg-amber-50 text-amber-700 border-amber-200/80'
                                        : 'bg-slate-100 text-slate-600 border-slate-200'
                                  }`}
                              >
                                {row.status.toLowerCase()}
                              </span>
                            </td>

                            {/* Payment Method */}
                            <td className="py-3.5 px-3 whitespace-nowrap">
                              <span
                                className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${row.payment_method === 'not set'
                                  ? 'bg-slate-100 text-slate-400'
                                  : 'bg-slate-100 text-slate-700'
                                  }`}
                              >
                                {row.payment_method}
                              </span>
                            </td>

                            {/* Created by */}
                            <td className="py-3.5 px-3 whitespace-nowrap text-slate-500">
                              <span className="bg-slate-100/80 text-slate-700 px-2 py-0.5 rounded text-[11px]">
                                {row.created_by?.split('@')[0] || 'User'}
                              </span>
                            </td>

                            <td className="py-3.5 pr-4 pl-2 text-right">
                              {activeTab === 'quotes' ? (
                                row.status === 'Converted' ? (
                                  <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200">
                                    Converted
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSaleModalQuote({
                                        name: row.name,
                                        customer: row.customer,
                                        customer_name: row.customer_name || row.customer,
                                        net_total: row.total,
                                        items: row.items,
                                      });
                                    }}
                                    className="btn-primary text-xs py-1 px-2.5 inline-flex items-center gap-1 shadow-sm"
                                    title="Make Sale Directly"
                                  >
                                    <ShoppingCart className="w-3.5 h-3.5" /> Sale
                                  </button>
                                )
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-300 inline" />
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION FOOTER */}
              {totalPages > 1 && (
                <div className="px-4 py-3 bg-slate-50/60 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600">
                  <div>
                    Showing <span className="font-semibold">{(page - 1) * limit + 1}</span> to{' '}
                    <span className="font-semibold">{Math.min(page * limit, totalItems)}</span> of{' '}
                    <span className="font-semibold">{totalItems}</span> entries
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-1 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="px-2 font-medium">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-1 rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN: DETAIL INSPECTION PANEL (4 or 5 COLS - Only visible when an item is clicked) */}
            {selectedDocId && (
              <div className="lg:col-span-5 xl:col-span-4 sticky top-6 bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-hidden flex flex-col max-h-[calc(100vh-140px)] animate-in fade-in zoom-in-95 duration-150">
                {isDetailLoading ? (
                  <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-rose-500 mb-3" />
                    <span className="text-xs">Loading details...</span>
                  </div>
                ) : !detailData ? (
                  <div className="p-12 text-center text-slate-400 flex flex-col items-center justify-center">
                    <FileText className="w-10 h-10 text-slate-300 mb-3" />
                    <span className="text-sm font-semibold text-slate-600">Select an item to view details</span>
                  </div>
                ) : (
                  <div className="flex flex-col h-full overflow-hidden">
                    {/* DETAIL PANEL HEADER */}
                    <div className="p-5 border-b border-slate-100 bg-white shrink-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="text-xl font-mono font-bold text-slate-900">
                              {formatShortId(detailData.name)}
                            </h2>
                            <span
                              className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${detailData.status === 'Active' || detailData.status === 'Draft' || detailData.status === 'quote'
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                                : detailData.status === 'Converted' || detailData.status === 'Paid'
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                  : detailData.status === 'Expired'
                                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                                    : detailData.status === 'Unpaid' || detailData.status === 'Partially Paid'
                                      ? 'bg-amber-50 text-amber-700 border-amber-200'
                                      : 'bg-slate-100 text-slate-600 border-slate-200'
                                }`}
                            >
                              {detailData.status.toLowerCase()}
                            </span>
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {(() => {
                              const saleD = detailData.invoice_date;
                              const pickD = detailData.delivery_date;
                              const futureSale = activeTab === 'sales' && isFutureCalendarDate(saleD);
                              const futurePick = activeTab === 'sales' && isFutureCalendarDate(pickD);
                              return (
                                <>
                                  {saleD ? (
                                    <span className={futureSale ? 'text-sky-700 font-semibold' : undefined}>
                                      Sale {formatDate(saleD)}
                                      {futureSale ? ' · Future' : ''}
                                    </span>
                                  ) : (
                                    <>Created on {formatDate(detailData.creation)}</>
                                  )}
                                  {activeTab === 'sales' && pickD ? (
                                    <span className={futurePick ? 'text-amber-700 font-semibold' : 'text-slate-500'}>
                                      {' · '}Pick/Del {formatDate(pickD)}
                                      {futurePick ? ' · Due' : ''}
                                    </span>
                                  ) : null}
                                </>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Header Actions & Dropdown */}
                        <div className="flex items-center gap-1.5 relative">
                          {activeTab === 'quotes' && detailData.status !== 'Converted' && (
                            <button
                              type="button"
                              onClick={() => {
                                setSaleModalQuote({
                                  name: detailData.id || detailData.name,
                                  customer: detailData.customer?.name,
                                  customer_name: detailData.customer?.customer_name || detailData.customer?.name,
                                  net_total: detailData.financials?.total ?? detailData.financials?.subtotal ?? 0,
                                  items: detailData.items,
                                });
                              }}
                              className="flex items-center gap-1.5 px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold shadow-sm transition-colors"
                              title="Make Sale Directly"
                            >
                              <ShoppingCart className="w-3.5 h-3.5" />
                              <span>Sale</span>
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={handlePrint}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors"
                            title="Print (P)"
                          >
                            <Printer className="w-3.5 h-3.5" />
                            <span className="text-[10px] text-slate-400 font-mono font-medium">P</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => setActionMenuOpen(!actionMenuOpen)}
                            className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 transition-colors"
                            title="More actions"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              setSelectedDocId(null);
                              setActionMenuOpen(false);
                            }}
                            className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                            title="Close details (view full width)"
                          >
                            <X className="w-4 h-4" />
                          </button>

                          {/* Dropdown Menu matching screenshot */}
                          {actionMenuOpen && (
                            <div className="absolute right-0 top-9 w-44 bg-white border border-slate-200/90 rounded-xl shadow-xl py-1.5 z-40 text-xs">
                              {(activeTab === 'quotes' || activeTab === 'sales') && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActionMenuOpen(false);
                                    setEditRecordData(detailData);
                                    setCreateModalMode(activeTab === 'quotes' ? 'quote' : 'sale');
                                    setIsCreateModalOpen(true);
                                  }}
                                  className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                                >
                                  <span className="flex items-center gap-2">
                                    <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Edit</span>
                                  </span>
                                  <span className="text-[10px] text-slate-400 font-mono">E</span>
                                </button>
                              )}

                              {activeTab === 'quotes' && (
                                <button
                                  type="button"
                                  onClick={() => duplicateMutation.mutate(detailData.id)}
                                  className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                                >
                                  <span className="flex items-center gap-2">
                                    <Copy className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Duplicate</span>
                                  </span>
                                </button>
                              )}

                              {activeTab === 'quotes' && (
                                <button
                                  type="button"
                                  disabled={detailData.status === 'Converted'}
                                  onClick={() => {
                                    setActionMenuOpen(false);
                                    setSaleModalQuote({
                                      name: detailData.id || detailData.name,
                                      customer: detailData.customer?.name,
                                      customer_name: detailData.customer?.customer_name || detailData.customer?.name,
                                      net_total: detailData.financials?.total ?? detailData.financials?.subtotal ?? 0,
                                      items: detailData.items,
                                    });
                                  }}
                                  className={`w-full text-left px-3.5 py-2 flex items-center justify-between ${
                                    detailData.status === 'Converted'
                                      ? 'opacity-50 cursor-not-allowed text-slate-400'
                                      : 'hover:bg-slate-50 text-slate-700'
                                  }`}
                                >
                                  <span className="flex items-center gap-2">
                                    <ShoppingCart className="w-3.5 h-3.5 text-indigo-600" />
                                    <span>{detailData.status === 'Converted' ? 'Converted' : 'Sale'}</span>
                                  </span>
                                </button>
                              )}

                              {/* <button
                              type="button"
                              onClick={() => {
                                setActionMenuOpen(false);
                                toast.success('Share link copied to clipboard');
                              }}
                              className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                            >
                              <span className="flex items-center gap-2">
                                <Send className="w-3.5 h-3.5 text-slate-500" />
                                <span>Send</span>
                              </span>
                              <ChevronRight className="w-3 h-3 text-slate-400" />
                            </button> */}

                              {activeTab === 'sales' && (
                                <>
                                  <button
                                    type="button"
                                    disabled={pickupBusy}
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      void runInvoiceBulk([detailData.id], 'print');
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <Printer className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Print Invoice</span>
                                  </button>
                                  <button
                                    type="button"
                                    disabled={pickupBusy}
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      void runInvoiceBulk([detailData.id], 'download');
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <Download className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Download PDF</span>
                                  </button>
                                  {canSendInvoiceWhatsapp(detailData) && (
                                    <button
                                      type="button"
                                      disabled={whatsappSendBusy}
                                      onClick={() => {
                                        setActionMenuOpen(false);
                                        void sendInvoiceWhatsapp(detailData.id || detailData.name);
                                      }}
                                      className="w-full text-left px-3.5 py-2 hover:bg-emerald-50 text-emerald-800 flex items-center gap-2 disabled:opacity-50"
                                    >
                                      {whatsappSendBusy ? (
                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      ) : (
                                        <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                                      )}
                                      <span>Send Invoice PDF on WhatsApp</span>
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      setIsReceiptModalOpen(true);
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2"
                                  >
                                    <Printer className="w-3.5 h-3.5 text-slate-500" />
                                    <span>View / print receipt</span>
                                  </button>
                                  {(detailData.status === 'Unpaid' || detailData.status === 'Partially Paid') && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setActionMenuOpen(false);
                                        setPayByLinkInvoice({
                                          name: detailData.name,
                                          amount: detailData.financials?.balance,
                                        });
                                      }}
                                      className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2"
                                    >
                                      <QrCode className="w-3.5 h-3.5 text-slate-500" />
                                      <span>Pay by link / QR (Teya)</span>
                                    </button>
                                  )}
                                </>
                              )}

                              {activeTab === 'quotes' && (
                                <>
                                  <button
                                    type="button"
                                    disabled={pickupBusy}
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      void runQuoteBulk([detailData.id], 'download');
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between disabled:opacity-50"
                                  >
                                    <span className="flex items-center gap-2">
                                      <Download className="w-3.5 h-3.5 text-slate-500" />
                                      <span>Download PDF</span>
                                    </span>
                                    <span className="text-[10px] text-slate-400 font-mono">I</span>
                                  </button>
                                  <button
                                    type="button"
                                    disabled={pickupBusy}
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      void runQuoteBulk([detailData.id], 'print');
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <Printer className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Print PDF</span>
                                  </button>
                                </>
                              )}

                              {activeTab !== 'sales' && activeTab !== 'quotes' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActionMenuOpen(false);
                                    setIsReceiptModalOpen(true);
                                  }}
                                  className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center justify-between"
                                >
                                  <span className="flex items-center gap-2">
                                    <Download className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Download</span>
                                  </span>
                                  <span className="text-[10px] text-slate-400 font-mono">I</span>
                                </button>
                              )}

                              {activeTab === 'sales' && (
                                <>
                                  <div className="px-3.5 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-t border-slate-100 mt-1">
                                    Pickup list (A4)
                                  </div>
                                  <button
                                    type="button"
                                    disabled={pickupBusy}
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      void runPickupList([detailData.id], 'download', true);
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <ClipboardList className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Download with prices</span>
                                  </button>
                                  <button
                                    type="button"
                                    disabled={pickupBusy}
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      void runPickupList([detailData.id], 'download', false);
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <ClipboardList className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Download without prices</span>
                                  </button>
                                  <button
                                    type="button"
                                    disabled={pickupBusy}
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      void runPickupList([detailData.id], 'print', true);
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <Printer className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Print with prices</span>
                                  </button>
                                  <button
                                    type="button"
                                    disabled={pickupBusy}
                                    onClick={() => {
                                      setActionMenuOpen(false);
                                      void runPickupList([detailData.id], 'print', false);
                                    }}
                                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 flex items-center gap-2 disabled:opacity-50"
                                  >
                                    <Printer className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Print without prices</span>
                                  </button>
                                </>
                              )}

                              {activeTab === 'quotes' && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDeleteConfirmModal(detailData.id);
                                    setActionMenuOpen(false);
                                  }}
                                  className="w-full text-left px-3.5 py-2 hover:bg-rose-50 text-rose-600 flex items-center justify-between border-t border-slate-100 mt-1 pt-1.5"
                                >
                                  <span className="flex items-center gap-2">
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Delete</span>
                                  </span>
                                </button>
                              )}

                              {activeTab === 'sales' && isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActionMenuOpen(false);
                                    setDeleteSaleTarget({
                                      id: detailData.id,
                                      name: detailData.name,
                                      customer: detailData.customer?.customer_name || detailData.customer?.name,
                                      total: detailData.financials?.total,
                                    });
                                  }}
                                  className="w-full text-left px-3.5 py-2 hover:bg-rose-50 text-rose-600 flex items-center justify-between border-t border-slate-100 mt-1 pt-1.5 font-semibold"
                                >
                                  <span className="flex items-center gap-2">
                                    <Trash2 className="w-3.5 h-3.5" />
                                    <span>Delete Sale (Revert Stock)</span>
                                  </span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* SCROLLABLE DETAIL BODY */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-6">
                      {/* SECTION: PRODUCTS */}
                      <div>
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                          Products ({detailData.items.length})
                        </h3>
                        <div className="space-y-3">
                          {detailData.items.map((prod, idx) => (
                            <div key={idx} className="flex items-start justify-between text-xs">
                              <div className="pr-2">
                                <div className="font-bold text-slate-900 leading-snug">
                                  {prod.item_name}
                                </div>
                                {prod.line_comment ? (
                                  <div className="text-[11px] text-slate-600 italic mt-0.5">
                                    {prod.line_comment}
                                  </div>
                                ) : null}
                                <div className="text-slate-400 text-[11px] mt-0.5">
                                  Quantity: {prod.quantity}{' '}
                                  <span className="text-slate-300">|</span> Available: {prod.available_stock}
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <div className="font-bold text-slate-900">
                                  {Number(prod.promo_amount) > 0 ? (
                                    <>
                                      <span className="line-through text-slate-400 font-medium mr-1.5">
                                        {formatMoney(
                                          (Number(prod.amount) || 0) + (Number(prod.promo_amount) || 0),
                                        )}
                                      </span>
                                      {formatMoney(prod.amount)}
                                    </>
                                  ) : (
                                    formatMoney(prod.amount)
                                  )}
                                </div>
                                <div className="text-slate-400 text-[11px]">
                                  (
                                  {Number(prod.original_rate) > 0 &&
                                  Number(prod.original_rate) > Number(prod.rate) ? (
                                    <>
                                      <span className="line-through mr-1">
                                        {formatMoney(prod.original_rate)}
                                      </span>
                                      {formatMoney(prod.rate)} each
                                    </>
                                  ) : (
                                    <>{formatMoney(prod.rate)} each</>
                                  )}
                                  )
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* SECTION: PAYMENT DETAILS */}
                      <div className="border-t border-slate-100 pt-4">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                          Payment Details
                        </h3>
                        <div className="space-y-2 text-xs">
                          {(() => {
                            const vatAmt = Number(detailData.financials.vat || 0);
                            // Prefer header promo; fall back to sum of line promo_amount
                            let promoAmt = Number(
                              detailData.financials?.promo_total ?? detailData.promo_total ?? 0,
                            );
                            if (promoAmt <= 0) {
                              promoAmt = (detailData.items || []).reduce(
                                (s, it) => s + (Number(it.promo_amount) || 0),
                                0,
                              );
                            }
                            let discAmt = Number(
                              detailData.financials?.discount_amount ??
                                detailData.financials?.discount ??
                                detailData.discount_amount ??
                                0,
                            );
                            const loyaltyAmt = Number(
                              detailData.financials?.loyalty_discount ??
                                detailData.loyalty?.loyalty_discount ??
                                0,
                            );
                            const loyaltyPts = Math.floor(
                              Number(detailData.loyalty?.redeemed || 0),
                            );
                            // Older payloads fold loyalty into discount_amount — peel it out
                            if (
                              detailData.financials?.loyalty_discount == null &&
                              loyaltyAmt > 0 &&
                              discAmt >= loyaltyAmt - 0.01
                            ) {
                              discAmt = Math.max(
                                0,
                                Math.round((discAmt - loyaltyAmt) * 100) / 100,
                              );
                            }
                            const writeOff = Number(detailData.financials?.write_off_amount || 0);
                            const profileIncl = Boolean(
                              (profile as Record<string, unknown> | undefined)?.prices_include_tax,
                            );
                            const pricesIncl = Boolean(
                              detailData.financials?.prices_include_tax ??
                                detailData.prices_include_tax ??
                                profileIncl,
                            );
                            // Tax-inclusive: never show a phantom "discount" that equals VAT
                            // (legacy bug: (subtotal + vat) − total invented that row).
                            if (pricesIncl && discAmt > 0 && vatAmt > 0 && Math.abs(discAmt - vatAmt) < 0.02) {
                              discAmt = 0;
                            }
                            // Never invent discount from subtotal+vat−total on the client
                            const displaySubtotal =
                              promoAmt > 0
                                ? Number(
                                    detailData.financials?.subtotal_original ??
                                      (detailData.financials.subtotal || 0) + promoAmt,
                                  )
                                : detailData.financials.subtotal;

                            let discPct =
                              detailData.financials?.discount_percent ?? detailData.discount_percent;
                            const discType =
                              detailData.financials?.discount_type ?? detailData.discount_type;
                            if (discPct == null && detailData.notes) {
                              const match =
                                detailData.notes.match(/Discount(?:\s*Rate)?:\s*([\d\.]+)%/i) ||
                                detailData.notes.match(
                                  /Discount\s*\((?:%|percentage)\):\s*([\d\.]+)/i,
                                );
                              if (match) discPct = parseFloat(match[1]);
                            }
                            if (
                              discPct == null &&
                              discType === 'percentage' &&
                              (detailData.financials?.discount_value || detailData.discount_value)
                            ) {
                              discPct =
                                detailData.financials?.discount_value ?? detailData.discount_value;
                            }

                            return (
                              <>
                                <div className="flex justify-between text-slate-600">
                                  <span>Subtotal</span>
                                  <span className="font-semibold text-slate-900">
                                    {formatMoney(displaySubtotal)}
                                  </span>
                                </div>
                                {promoAmt > 0 && (
                                  <div className="flex justify-between text-slate-600">
                                    <span>Promotions</span>
                                    <span className="font-semibold text-emerald-600">
                                      − {formatMoney(promoAmt)}
                                    </span>
                                  </div>
                                )}
                                {loyaltyAmt > 0 && (
                                  <div className="flex justify-between text-slate-600">
                                    <span>
                                      Loyalty points redeem
                                      {loyaltyPts > 0 ? ` (${loyaltyPts} pts)` : ''}
                                    </span>
                                    <span className="font-semibold text-emerald-600">
                                      − {formatMoney(loyaltyAmt)}
                                    </span>
                                  </div>
                                )}
                                {discAmt > 0 && (
                                  <div className="flex justify-between text-slate-600">
                                    <span>
                                      Discount
                                      {discPct != null && discPct > 0 ? ` (${discPct}%)` : ''}
                                    </span>
                                    <span className="font-semibold text-emerald-600">
                                      − {formatMoney(discAmt)}
                                    </span>
                                  </div>
                                )}
                                {writeOff > 0 && (
                                  <div className="flex justify-between text-slate-600">
                                    <span>Write off</span>
                                    <span className="font-semibold text-emerald-600">
                                      − {formatMoney(writeOff)}
                                    </span>
                                  </div>
                                )}
                                {vatAmt > 0 && (
                                  <div className="flex justify-between text-slate-600">
                                    <span>{pricesIncl ? 'VAT (included)' : 'VAT'}</span>
                                    <span className="font-semibold text-slate-900">
                                      {formatMoney(vatAmt)}
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between text-sm font-bold text-slate-900 pt-1 border-t border-slate-100">
                                  <span>Total</span>
                                  <span>{formatMoney(detailData.financials.total)}</span>
                                </div>
                                {pricesIncl && vatAmt > 0 && (
                                  <p className="text-[10px] text-slate-400 pt-0.5">
                                    Prices include tax · VAT is the tax portion of the total, not an
                                    extra charge
                                  </p>
                                )}
                                <div className="flex justify-between text-slate-600 pt-1">
                                  <span>Paid</span>
                                  <span className="font-semibold text-slate-900">
                                    {formatMoney(detailData.financials.paid)}
                                  </span>
                                </div>
                                <div className="flex justify-between font-bold text-rose-600">
                                  <span>Balance</span>
                                  <span>{formatMoney(detailData.financials.balance)}</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* SECTION: PROFIT */}
                      <div className="border-t border-slate-100 pt-4">
                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
                          Profit
                        </h3>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Profit</span>
                            <span className="font-bold text-emerald-600">
                              {formatMoney(detailData.financials.profit)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Profit Margin</span>
                            <span className="font-bold text-emerald-600">
                              {detailData.financials.profit_margin}%
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* SECTION: CUSTOMER & DELIVERY DETAILS */}
                      {detailData.customer && (
                        <div className="border-t border-slate-100 pt-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                                Customer Details
                              </h3>
                              <div className="space-y-1 text-xs">
                                <div className="font-bold text-slate-900 text-sm">
                                  {detailData.customer.customer_name || 'Walk-in'}
                                </div>
                                {detailData.customer.mobile && (
                                  <div className="text-slate-600">
                                    {detailData.customer.mobile}
                                  </div>
                                )}
                                {detailData.customer.email && (
                                  <div className="text-slate-500 text-[11px]">
                                    {detailData.customer.email}
                                  </div>
                                )}
                                {activeTab === 'sales' && canSendInvoiceWhatsapp(detailData) && (
                                  <button
                                    type="button"
                                    disabled={whatsappSendBusy}
                                    onClick={() =>
                                      void sendInvoiceWhatsapp(detailData.id || detailData.name)
                                    }
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                                  >
                                    {whatsappSendBusy ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <MessageCircle className="w-3 h-3" />
                                    )}
                                    Send Invoice PDF on WhatsApp
                                  </button>
                                )}
                              </div>
                            </div>

                            <div>
                              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                                Delivery Address
                              </h3>
                              <div className="text-xs text-slate-600 space-y-0.5">
                                {detailData.customer.address ? (
                                  <>
                                    <div>{detailData.customer.address}</div>
                                    <div>
                                      {[detailData.customer.city, detailData.customer.postal_code].filter(Boolean).join(', ')}
                                    </div>
                                  </>
                                ) : (
                                  <span className="text-slate-400 italic">No delivery address</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* CREATE / EDIT ORDER MODAL (FULL SCREEN WITHIN MAIN CONTENT AREA) */}
        <CreateSalesOrderModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditRecordData(null);
          }}
          mode={createModalMode}
          editDocId={editRecordData?.id || null}
          initialData={editRecordData}
          onSuccess={(createdId) => {
            setSelectedDocId(createdId);
            setEditRecordData(null);
          }}
        />
      </div>

      {/* REACT CONFIRMATION MODAL FOR DELETION (NO native alert/confirm) */}
      {deleteConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center animate-in fade-in zoom-in-95 duration-150">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Delete Quotation?</h3>
            <p className="text-xs text-slate-500 mt-2">
              Are you sure you want to delete quotation <span className="font-mono font-bold text-slate-800">{deleteConfirmModal}</span>? This action cannot be undone.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmModal(null)}
                className="flex-1 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteConfirmModal)}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QUOTATION PDF MODAL */}
      {isQuotationPdfModalOpen && detailData && (
        <QuotationPdfModal
          isOpen={isQuotationPdfModalOpen}
          onClose={() => setIsQuotationPdfModalOpen(false)}
          quotation={detailData}
          profile={profile}
        />
      )}

      {/* SALE / RETURN RECEIPT MODAL */}
      {payByLinkInvoice && (
        <PayByLinkModal
          invoiceName={payByLinkInvoice.name}
          amount={payByLinkInvoice.amount}
          onClose={() => setPayByLinkInvoice(null)}
        />
      )}
      {isReceiptModalOpen && detailData && (
        <Receipt
          invoiceName={activeTab === 'sales' ? detailData.name : undefined}
          previewInvoice={activeTab === 'returns' ? receiptPreviewData : undefined}
          onClose={() => setIsReceiptModalOpen(false)}
        />
      )}

      {/* QUICK SALE MODAL FOR QUOTES */}
      <QuotationQuickSaleModal
        isOpen={!!saleModalQuote}
        onClose={() => setSaleModalQuote(null)}
        quotation={saleModalQuote}
        onSuccess={(invoiceName) => {
          queryClient.invalidateQueries({ queryKey: ['salesHubList'] });
          queryClient.invalidateQueries({ queryKey: ['salesHubCounts'] });
          queryClient.invalidateQueries({ queryKey: ['salesHubDetail'] });
          if (invoiceName) {
            setCreatedInvoiceReceipt(invoiceName);
          }
        }}
      />

      {/* DIRECT INVOICE RECEIPT MODAL */}
      {createdInvoiceReceipt && (
        <Receipt
          invoiceName={createdInvoiceReceipt}
          onClose={() => setCreatedInvoiceReceipt(null)}
        />
      )}

      {/* DELETE INDIVIDUAL SALE MODAL */}
      {deleteSaleTarget && (
        <DeleteSaleModal
          invoiceId={deleteSaleTarget.id}
          invoiceName={deleteSaleTarget.name}
          customerName={deleteSaleTarget.customer}
          totalAmount={deleteSaleTarget.total}
          onClose={() => setDeleteSaleTarget(null)}
          onSuccess={() => {
            setSelectedDocId(null);
            setDeleteSaleTarget(null);
          }}
        />
      )}

      {/* DELETE SELECTED SALES ONLY */}
      {showBulkDeleteModal && (
        <BulkDeleteSalesModal
          invoiceIds={checkedIds}
          onClose={() => setShowBulkDeleteModal(false)}
          onSuccess={() => {
            setCheckedIds([]);
            setSelectedDocId(null);
            setShowBulkDeleteModal(false);
          }}
        />
      )}

      {/* CLEAR ALL SALES DATA MODAL (full wipe — ignores selection) */}
      {showClearAllSalesModal && (
        <ClearDataModal onClose={() => setShowClearAllSalesModal(false)} />
      )}

      {/* Scan pick-list QR / barcode to open a sale */}
      {showSaleScanner && (
        <CameraScannerModal
          onScan={openSaleFromCode}
          onClose={() => {
            setShowSaleScanner(false);
            setSaleScanError('');
          }}
          scanPending={saleScanPending}
          scanError={saleScanError}
        />
      )}
    </div>
  );
}
